import React, { useEffect, useState } from 'react'
import { Box, CardHeader, Typography, IconButton, Checkbox, FormControlLabel, Grid, Card, OutlinedInput, InputAdornment } from '@mui/material';
import {
    Search, ChevronLeft, ChevronRight
} from 'lucide-react';
import CustomButton from '../componentsII/Buttons';
import { useSelector } from 'react-redux';
import api from '../axios/api';
import { showNotification } from '../redux/slices/notificationSlice';
import { useDispatch } from 'react-redux';

const SettingsModal = ({
    Title = "Configure Vehicles",
    HelperText = ["Below is the list of vehicles that are active for 2024 and the list of vehicles available from other reporting years but inactive for the current reporting year.", "You can add new vehicles to this reporting year from your main vehicles page"],
    ActiveList = [],
    InactiveList = [],
    ModalFor = "",
    handleCancel,
    EmptyActiveImage,
    EmptyActiveText = "No active vehicles",
    EmptyInactiveImage,
    EmptyInactiveText = "No inactive vehicles",
}) => {
    const [activeVehicles, setActiveVehicles] = useState(ActiveList.map(v => ({ ...v, selected: false })));
    const [inactiveVehicles, setInactiveVehicles] = useState(InactiveList.map(v => ({ ...v, selected: false })));
    const [searchTerm, setSearchTerm] = useState('');
    const year = useSelector((state) => state.user.selectedYear);
    const user = useSelector((state) => state.user.user);
    const dispatch = useDispatch();

    const moveToActive = () => {
        const selectedInactive = inactiveVehicles.filter(v => v.selected);
        setActiveVehicles([
            ...activeVehicles,
            ...selectedInactive.map(v => ({ ...v, selected: false }))
        ]);
        setInactiveVehicles(inactiveVehicles.filter(v => !v.selected));
    }

    const onSave = async () => {
        try {
            const activateids = [...activeVehicles]
            const deactivateids = [...inactiveVehicles]

            const response = await api.post(`/company/addprevyeardata?company_id=${user.company.id}&year_id=${year.id}&active_for=${ModalFor}`, { 'activateids': activateids, 'deactivateids': deactivateids });

            if (response.data.status === 200) {
                dispatch(showNotification({ 'message': response.data.message, type: 'success' }));
            } else {
                dispatch(showNotification({ 'message': response.data.message, type: 'error' }));
            }
        } catch (error) {
            dispatch(showNotification({ 'message': error.message, type: 'error' }));
            console.log(error);
        }
    };

    const moveToInactive = () => {
        const selectedActive = activeVehicles.filter(v => v.selected);
        setInactiveVehicles([
            ...inactiveVehicles,
            ...selectedActive.map(v => ({ ...v, selected: false }))
        ]);
        setActiveVehicles(activeVehicles.filter(v => !v.selected));
    }

    const toggleActiveVehicleSelect = (id) => {
        setActiveVehicles(prev => prev.map(v =>
            v.id === id ? { ...v, selected: !v.selected } : v
        ));
    }

    const toggleInactiveVehicleSelect = (id) => {
        setInactiveVehicles(prev => prev.map(v =>
            v.id === id ? { ...v, selected: !v.selected } : v
        ));
    }

    const handleSave = () => {
        onSave && onSave(activeVehicles);
    }

    const filteredActiveVehicles = activeVehicles.filter(vehicle =>
        vehicle.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.details.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredInactiveVehicles = inactiveVehicles.filter(vehicle =>
        vehicle.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.details.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const renderVehicleList = (vehicles, filteredVehicles, toggleSelect, emptyImage, emptyText) => (
        <>
            <hr className='bg-gray-500 h-0.5' p-1 />
            <Box
                p={2}
                sx={{
                    height: '300px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {filteredVehicles.length > 0 ? (
                    filteredVehicles.map((vehicle) => (
                        <Box
                            key={vehicle.id}
                            display="flex"
                            alignItems="center"
                            gap={2}
                            p={1}
                            borderBottom="1px solid #ddd"

                        >
                            <Checkbox
                                color='success'
                                checked={vehicle.selected}
                                onChange={() => toggleSelect(vehicle.id)}
                            />
                            <Box>
                                <Typography variant="subtitle1">{vehicle.name}</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {vehicle.details}
                                </Typography>
                            </Box>
                        </Box>
                    ))
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%'

                        }}
                    >
                        {emptyImage && (
                            <img
                                src={emptyImage}
                                alt={emptyText}
                                style={{ maxWidth: '200px', marginBottom: '16px' }}
                            />
                        )}
                        <Typography variant="body1" color="textSecondary">
                            {emptyText}
                        </Typography>
                    </Box>
                )}
            </Box>
        </>
    );

    return (
        <div>
            <Box sx={{ width: 900, margin: "auto" }}>
                <CardHeader title={<Typography variant="h5">{Title}</Typography>} />
                <hr />
                {HelperText.map((item, i) => (
                    <Typography key={i} variant="body1" sx={{ marginTop: 2, marginBottom: 1 }}>
                        {item}
                    </Typography>
                ))}
                <hr />
                <OutlinedInput
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="Search"
                                edge="end"
                            >
                                <Search size={20} />
                            </IconButton>
                        </InputAdornment>
                    }
                    className='m-5'
                    size='small'
                    placeholder={`search active inactive ${ModalFor}`}
                    sx={{ width: '50%' }}
                />
                <div>
                    <Grid container spacing={2} alignItems="center">
                        {/* Active Vehicles Section */}
                        <Grid item md={5.5}>
                            <Card sx={{ backgroundColor: "#d8f0e1" }}>
                                <CardHeader
                                    title={
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    color='success'
                                                    checked={activeVehicles.length > 0 && activeVehicles.every(v => v.selected)}
                                                    indeterminate={activeVehicles.some(v => v.selected) && !activeVehicles.every(v => v.selected)}
                                                    onChange={() => {
                                                        const allSelected = activeVehicles.every(v => v.selected);
                                                        setActiveVehicles(prev => prev.map(v => ({ ...v, selected: !allSelected })));
                                                    }}
                                                />
                                            }
                                            label={`Active ${ModalFor} ${year?.start_year}`}
                                        />
                                    }
                                />
                                {renderVehicleList(
                                    activeVehicles,
                                    filteredActiveVehicles,
                                    toggleActiveVehicleSelect,
                                    EmptyActiveImage,
                                    EmptyActiveText
                                )}
                            </Card>
                        </Grid>

                        {/* Chevron Icons for Moving Vehicles */}
                        <Grid item display="flex" justifyContent="center" flexDirection="column">
                            <IconButton
                                onClick={moveToInactive}
                                disabled={!activeVehicles.some(v => v.selected)}
                            >
                                <ChevronRight size={24} />
                            </IconButton>
                            <IconButton
                                onClick={moveToActive}
                                disabled={!inactiveVehicles.some(v => v.selected)}
                            >
                                <ChevronLeft size={24} />
                            </IconButton>
                        </Grid>

                        {/* Inactive Vehicles Section */}
                        <Grid item md={5.5}>
                            <Card sx={{ backgroundColor: "#e6e6e6" }}>
                                <CardHeader
                                    title={
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    color='success'
                                                    checked={inactiveVehicles.length > 0 && inactiveVehicles.every(v => v.selected)}
                                                    indeterminate={inactiveVehicles.some(v => v.selected) && !inactiveVehicles.every(v => v.selected)}
                                                    onChange={() => {
                                                        const allSelected = inactiveVehicles.every(v => v.selected);
                                                        setInactiveVehicles(prev => prev.map(v => ({ ...v, selected: !allSelected })));
                                                    }}
                                                />
                                            }
                                            label={`Inactive ${ModalFor} ${year?.start_year}`}
                                        />
                                    }
                                />
                                {renderVehicleList(
                                    inactiveVehicles,
                                    filteredInactiveVehicles,
                                    toggleInactiveVehicleSelect,
                                    EmptyInactiveImage,
                                    EmptyInactiveText
                                )}
                            </Card>
                        </Grid>
                    </Grid>
                </div>
                <div className='flex gap-4 justify-end mt-4'>
                    <CustomButton
                        className="bg-gray-500 hover:bg-gray-700 text-white"
                        text="Cancel"
                        click={() => { handleCancel() }}
                    />
                    <CustomButton
                        className="bg-green-600 hover:bg-green-700 text-white"
                        text="Submit"
                        click={handleSave}
                    />
                </div>
            </Box>
        </div>
    )
}

export default SettingsModal